import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Send, RefreshCw } from "lucide-react";
import PasswordDeleteButton from "@/components/PasswordDeleteButton";

export default function AdminQuotes() {
    const [quotes, setQuotes] = useState([]);
    const [selected, setSelected] = useState(null);
    const [resending, setResending] = useState(false);

    const load = async () => {
        const { data } = await api.get("/admin/quotes");
        setQuotes(data);
    };
    useEffect(() => { load(); }, []);

    const resend = async (q) => {
        setResending(true);
        try {
            const { data } = await api.post(`/admin/quotes/${q.id}/resend`);
            toast.success(`Reenvio: WhatsApp ${data.whatsapp.ok ? "✓" : "✗"} · E-mail ${data.email.ok ? "✓" : "✗"}`);
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setResending(false); }
    };

    return (
        <div className="p-8">
            <div className="flex items-start justify-between">
                <div>
                    <div className="text-xs font-mono uppercase tracking-[0.24em] text-neutral-500">Fase 4 · Lista</div>
                    <h1 className="mt-1 font-heading font-black text-3xl tracking-tight">Orçamentos ({quotes.length})</h1>
                </div>
                <button onClick={load} className="h-11 px-4 bg-neutral-100 hover:bg-neutral-200 text-sm font-medium flex items-center gap-2"><RefreshCw size={14} /> Atualizar</button>
            </div>

            <div className="mt-6 grid lg:grid-cols-[420px,1fr] gap-6">
                <div className="bg-white border border-neutral-300">
                    <div className="p-3 border-b border-neutral-200 font-mono text-xs uppercase tracking-widest text-neutral-500">Últimos orçamentos</div>
                    <div className="max-h-[70vh] overflow-y-auto">
                        {quotes.length === 0 ? <div className="p-10 text-center font-mono text-sm text-neutral-500">nenhum orçamento ainda</div> :
                        quotes.map((q) => (
                            <button key={q.id} onClick={() => setSelected(q)} data-testid={`quote-item-${q.id}`}
                                className={`w-full text-left p-3 border-b border-neutral-100 hover:bg-neutral-50 ${selected?.id === q.id ? "bg-brand-blue/5 border-l-4 border-l-brand-blue" : ""}`}>
                                <div className="flex items-center justify-between">
                                    <span className="font-medium text-sm">{q.name}</span>
                                    <span className="text-[10px] font-mono text-neutral-500">{new Date(q.created_at).toLocaleDateString("pt-BR")}</span>
                                </div>
                                <div className="text-xs text-neutral-500">{q.company || "—"} · {q.items?.length || 0} itens</div>
                                <div className="mt-1 flex gap-2 text-[10px] font-mono">
                                    <span className={q.notifications?.whatsapp?.ok ? "text-green-700" : "text-red-600"}>WA {q.notifications?.whatsapp?.ok ? "✓" : "✗"}</span>
                                    <span className={q.notifications?.email?.ok ? "text-green-700" : "text-red-600"}>MAIL {q.notifications?.email?.ok ? "✓" : "✗"}</span>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="bg-white border border-neutral-300 p-6">
                    {!selected ? <div className="font-mono text-sm text-neutral-500 text-center py-16">selecione um orçamento à esquerda</div> : (
                        <div>
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">Orçamento #{selected.id.slice(0,8).toUpperCase()}</div>
                                    <h2 className="mt-1 font-heading font-black text-2xl">{selected.name}</h2>
                                    <div className="mt-2 font-mono text-xs text-neutral-500">{new Date(selected.created_at).toLocaleString("pt-BR")}</div>
                                </div>
                                <div className="flex items-start gap-2">
                                    <button disabled={resending} onClick={() => resend(selected)} data-testid="resend-quote" className="h-10 px-4 bg-brand-blue text-white font-heading font-bold uppercase tracking-wider text-xs hover:bg-brand-blue-hover disabled:opacity-60 flex items-center gap-2">
                                        <Send size={14} /> {resending ? "reenviando…" : "Reenviar"}
                                    </button>
                                    <PasswordDeleteButton
                                        endpoint={`/admin/quotes/${selected.id}`}
                                        label="Orçamento"
                                        size="lg"
                                        testId={`delete-quote-${selected.id}`}
                                        onDeleted={() => { setSelected(null); load(); }}
                                    />
                                </div>
                            </div>
                            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                                <Info label="Empresa" value={selected.company} />
                                <Info label="CNPJ" value={selected.cnpj} />
                                <Info label="E-mail" value={selected.email} />
                                <Info label="Telefone" value={selected.phone} />
                            </div>
                            <div className="mt-6">
                                <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-2">Itens ({selected.items?.length || 0})</div>
                                <table className="w-full text-sm">
                                    <thead className="bg-neutral-50 font-mono text-xs uppercase tracking-widest text-neutral-500">
                                        <tr><th className="text-left p-2">Qtd</th><th className="text-left p-2">Produto</th><th className="text-left p-2">Marca</th><th className="text-left p-2">Obs</th></tr>
                                    </thead>
                                    <tbody>
                                        {(selected.items || []).map((it, i) => (
                                            <tr key={i} className="border-t border-neutral-200">
                                                <td className="p-2 font-mono">{it.quantity}x</td>
                                                <td className="p-2 font-medium">{it.name}</td>
                                                <td className="p-2 font-mono text-xs text-neutral-500">{it.brand_name || "—"}</td>
                                                <td className="p-2 text-xs text-neutral-500 italic">{it.notes || "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {selected.message && (
                                <div className="mt-6">
                                    <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-2">Mensagem</div>
                                    <div className="bg-neutral-50 p-3 border-l-4 border-brand-yellow text-sm">{selected.message}</div>
                                </div>
                            )}
                            <div className="mt-6 grid grid-cols-2 gap-3">
                                <NotifCard title="WhatsApp" data={selected.notifications?.whatsapp} />
                                <NotifCard title="E-mail" data={selected.notifications?.email} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const Info = ({ label, value }) => (
    <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">{label}</div>
        <div className="mt-1">{value || "—"}</div>
    </div>
);

const NotifCard = ({ title, data }) => (
    <div className={`p-3 border ${data?.ok ? "border-green-300 bg-green-50" : "border-red-200 bg-red-50"}`}>
        <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">{title}</div>
        <div className={`text-sm font-medium ${data?.ok ? "text-green-800" : "text-red-800"}`}>
            {data?.ok ? "✓ enviado" : `✗ ${data?.reason || "não enviado"}`}
        </div>
    </div>
);
